import React, { useEffect, useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory, { PaginationProvider }  from "react-bootstrap-table2-paginator";
import { getAuthorities } from "../../../../../../_helpers/Helpers";
import * as actions from "../../../_redux/sections/SectionsAction";
import * as uiHelpers from "../SectionsUIHelpers";
import { 
  getSelectRow, 
  getHandlerTableChange,  
  NoRecordsFoundMessage, 
  PleaseWaitMessage, 
  sortCaret
} from "../../../../../../_metronic/_helpers";
import * as columnFormatters from "./column-formatters";
import { Pagination } from "../../../../../../_metronic/_partials/controls";
import { useSectionsUIContext } from "../SectionsUIContext";

export function SectionsTable({ deleteSections }) {
  // Sections UI Context
  const sectionsUIContext = useSectionsUIContext();
  const sectionsUIProps = useMemo(() => {
    return {
      ids: sectionsUIContext.ids,
      setIds: sectionsUIContext.setIds,
      queryParams: sectionsUIContext.queryParams,
      setQueryParams: sectionsUIContext.setQueryParams,
      openEditSectionPage: sectionsUIContext.openEditSectionPage
    };
  }, [sectionsUIContext]);

  // Getting curret state of Sections list from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.sections }),
    shallowEqual
  );
  const { totalCount, entities, authorities, listLoading } = currentState;
  
  // Authority Parameters
  const { isEditionAuthorized, isDeletionAuthorized } = getAuthorities(authorities);
  
  // Sections Redux state
  const dispatch = useDispatch();

  useEffect(() => {
    sectionsUIProps.setIds([]);
    dispatch(actions.getSections(sectionsUIProps.queryParams));
    return () => dispatch(actions.cleanSectionsState());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionsUIProps.queryParams, dispatch]);

  // Table columns
  const columns = [
    {
      dataField: "gradeLevelName",
      text: "Sınıf Düzeyi",
      sort: true,
      sortCaret: sortCaret,
    },
    {
      dataField: "grade",
      text: "Sınıf",
      sort: true,
      sortCaret: sortCaret,
    },    
    {
      dataField: "name",
      text: "Şube",
      sort: true,
      sortCaret: sortCaret,
    },
    {
      dataField: "capacity",
      text: "Kapasite",
      sort: true,
      sortCaret: sortCaret,
    }
  ];

  const actionColumn = {
    dataField: "action",
    text: "İşlemler",
    formatter: columnFormatters.ActionsColumnFormatter,
    formatExtraData: {
      isEditionAuthorized,
      isDeletionAuthorized,
      openEditSectionPage: sectionsUIProps.openEditSectionPage,
      deleteSections
    },
    classes: "text-right pr-0",
    headerClasses: "text-right pr-3",
    style: {
      minWidth: "100px",
    }
  }

  if (isEditionAuthorized || isDeletionAuthorized) columns.push(actionColumn);

  // Table pagination properties
  const paginationOptions = {
    custom: true,
    totalSize: totalCount,
    sizePerPageList: uiHelpers.sizePerPageList,
    sizePerPage: sectionsUIProps.queryParams.pageSize,
    page: sectionsUIProps.queryParams.pageNumber
  };
  
  return (
    <PaginationProvider pagination={paginationFactory(paginationOptions)}>
      {({ paginationProps, paginationTableProps }) => {
        return (
          <Pagination
            isLoading={listLoading}
            paginationProps={paginationProps}
          >
            <BootstrapTable
              wrapperClasses="table-responsive"
              classes="table table-head-custom table-vertical-center overflow-hidden"
              bootstrap4
              bordered={false}
              remote
              keyField="id"
              data={entities ?? [] }
              columns={columns}
              defaultSorted={uiHelpers.defaultSorted}
              onTableChange={getHandlerTableChange(
                sectionsUIProps.setQueryParams
              )}
              selectRow={getSelectRow({
                entities,
                ids: sectionsUIProps.ids,
                setIds: sectionsUIProps.setIds,
                hidden: !isDeletionAuthorized
              })}
              {...paginationTableProps}
            >
              <PleaseWaitMessage entities={entities} />
              <NoRecordsFoundMessage entities={entities} />
            </BootstrapTable>
          </Pagination>
        );    
      }}
    </PaginationProvider>  
  );
}